/* eslint-disable no-async-promise-executor */
import { FC, useState } from 'react';
import { Button, Stack, SxProps } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { getBase64, getFetchEror } from '@/app/helpers';
import { useNotification } from '@/shared/contexts/NotificationContext';
import {
  useCreateBuyerDealMutation,
  useCreateSellerDealMutation,
  useEditBuyerRequestMutation,
  useEditSellerRequestMutation,
  useSendBuyerRequestMutation,
  useSendSellerRequestMutation,
} from '@/app/api';
import { useNavigate } from 'react-router-dom';
import { eimzo } from '@/entities/eimzo';
import { useAppSelector } from '@/app/hooks';

import { useTranslation } from 'react-i18next';
import { CreateSellerRequestValues } from '../types';
import { CreateDealPayload, DealRequest } from '@/app/api/types';

interface Props {
  form: UseFormReturn<CreateSellerRequestValues, unknown, undefined>;
  additinonalDocs: { value: File | string; unique?: number; name?: string }[];
  type: 'buyer' | 'seller';
  sx?: SxProps;
  copiedDeal?: DealRequest;
}

export const CreateSellerRequestActions: FC<Props> = ({
  sx,
  form,
  additinonalDocs,
  copiedDeal,
  type,
}) => {
  const { t } = useTranslation('createDealForm');
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { profile, isUsbLog } = useAppSelector((state) => state.auth);

  const { handleSubmit } = form;

  const [createSellerDealMutation] = useCreateSellerDealMutation();
  const [createBuyerDealMutation] = useCreateBuyerDealMutation();
  const [editSellerDealMutation] = useEditSellerRequestMutation();
  const [editBuyerDealMutation] = useEditBuyerRequestMutation();
  const [sendSellerDealMutation] = useSendSellerRequestMutation();
  const [sendBuyerDealMutation] = useSendBuyerRequestMutation();

  const [isLoading, setIsLoading] = useState(false);

  const createMutation =
    type === 'buyer' ? createBuyerDealMutation : createSellerDealMutation;
  const editMutation =
    type === 'buyer' ? editBuyerDealMutation : editSellerDealMutation;
  const sendMutation =
    type === 'buyer' ? sendBuyerDealMutation : sendSellerDealMutation;

  const createDealHandler = (data: CreateSellerRequestValues) => {
    const isReverseDeal = type === 'buyer';
    const isOpenedDeal = !data?.isDebtor?.value;

    let sellerTin = '';
    let buyerTin = '';
    let debtorTin = '';

    if (isReverseDeal) {
      sellerTin = data.sellerTin || '';
      debtorTin = profile?.tin || '';
      buyerTin = profile?.tin || '';
    } else {
      sellerTin = profile?.tin || '';
      buyerTin = data.buyer?.tin || '';
      if (isOpenedDeal) {
        debtorTin = data.buyer?.tin || '';
      } else {
        debtorTin = profile?.tin || '';
      }
    }

    return new Promise<DealRequest>(async (resolve, reject) => {
      try {
        const additionalDocsData = await Promise.all(
          additinonalDocs.map(async (el) => {
            const obj: CreateDealPayload['documents'][0] = {};
            if (el.unique) {
              obj.unique = el.unique;
            }

            if (typeof el.value !== 'string') {
              obj.base64 = await getBase64(el.value).then(
                (res) => res.split(',')[1]
              );
              obj.fileName = el.value.name;
              obj.type = el.value.name;
            } else {
              const url = new URL(el.value);
              const blob = await fetch('/api' + url.pathname).then((res) =>
                res.blob()
              );
              const file = new File([blob], el.name || 'file', {
                type: blob.type,
              });
              const base64 = await getBase64(file).then(
                (res) => res.split(',')[1]
              );
              obj.base64 = base64;
              obj.fileName = file.name;
              obj.type = file.name;
            }
            return obj;
          })
        );
        const didoxDocuments: CreateDealPayload['documents'] =
          await Promise.all(
            data.documents
              .filter((el) => !!el.value)
              .map(async (doc) => {
                const obj: CreateDealPayload['documents'][0] = {};

                if (doc?.value?.unique) {
                  obj.unique = doc.value.unique;
                }

                if (doc.value && 'doc_id' in doc.value) {
                  obj.didoxId = doc.value?.doc_id;
                  obj.type = doc.value?.doctype;
                } else if (doc.value?.value) {
                  if (typeof doc.value.value !== 'string') {
                    obj.base64 = await getBase64(doc.value.value).then(
                      (res) => res.split(',')[1]
                    );
                    obj.type = doc.type;
                    obj.fileName = doc.value.value.name;
                  } else {
                    const url = new URL(doc.value.value);
                    const blob = await fetch('/api' + url.pathname).then(
                      (res) => res.blob()
                    );
                    const file = new File([blob], doc.type, {
                      type: blob.type,
                    });
                    const base64 = await getBase64(file).then(
                      (res) => res.split(',')[1]
                    );
                    obj.base64 = base64;
                    obj.type = doc.type;
                    obj.fileName = doc.value?.name || doc.type;
                  }
                }

                return obj;
              })
          );
        const body: CreateDealPayload = {
          buyer: buyerTin,
          seller: sellerTin,
          factor: data.factor?.tin || '',
          buyerDebtAmount: Number(data.buyerDebtAmount.replace(/\s/g, '')),
          contractAmount: Number(data.contractAmount.replace(/\s/g, '')),
          contractPaymentDueDate:
            data.contractPaymentDueDate.format('DD.MM.YYYY'),
          branchId: data?.branchId?.id || null,
          // discount: Number(data.discount),
          // paymentDueDate: data.paymentDueDate.format("DD.MM.YYYY"),

          debtorTin,
          documents: [...didoxDocuments, ...additionalDocsData].map(
            (el, idx) => ({
              ...el,
              orderIndex: idx + 1,
            })
          ),
          currency: data.currency ?? 'UZS',
        };

        const isInternational = Boolean(data.foreignContractorId);

        if (isInternational) {
          body.foreignContractorId = data.foreignContractorId;
          delete body.buyer;
          delete body.seller;
          delete body.debtorTin;
        } else if (type === 'buyer') {
          delete body.buyer;
        } else if (type === 'seller') {
          delete body.seller;
        }

        let deal: DealRequest;
        if (copiedDeal) {
          deal = await editMutation({
            unique: copiedDeal.unique,
            body,
          })
            .unwrap()
            .then((res) => res.data);
        } else {
          deal = await createMutation(body)
            .unwrap()
            .then((res) => res.data);

          window.gtag('event', 'sign_deal_request', {
            deal_unique: deal.unique,
            user_tin: profile?.tin,
            buyer_tin: data.buyer?.tin,
          });
        }
        resolve(deal);
      } catch (err) {
        reject(err);
      }
    });
  };

  const onSaveInDraftsHandler = async (data: CreateSellerRequestValues) => {
    try {
      setIsLoading(true);
      await createDealHandler(data);
      navigate('/seller/deals');
      showNotification(
        t(
          copiedDeal ? 'deal_edited_successfully' : 'deal_created_successfully'
        ),
        'success'
      );
    } catch (err) {
      showNotification(getFetchEror(err));
    } finally {
      setIsLoading(false);
    }
  };

  const onSaveAndSendHandler = async (data: CreateSellerRequestValues) => {
    try {
      setIsLoading(true);
      const deal = await createDealHandler(data);
      const signature = await eimzo.sign(
        profile?.tin || '',
        profile?.tin || '',
        isUsbLog
      );
      await sendMutation({ signature, unique: deal.unique }).unwrap();
      showNotification(t('deal_created_successfully'), 'success');
      navigate('/seller/deals');
    } catch (err) {
      showNotification(getFetchEror(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDeal = () => {
    navigate('/seller/deals');
  };

  return (
    <Stack direction='row' justifyContent='flex-end' spacing={2} sx={sx}>
      <Button color='inherit' disabled={isLoading} onClick={handleCancelDeal}>
        {t('cancel')}
      </Button>
      <Button
        color='inherit'
        onClick={handleSubmit(onSaveInDraftsHandler)}
        disabled={isLoading}
      >
        {t(copiedDeal ? 'common:edit' : 'save_as_draft')}
      </Button>
      <Button
        color='primary'
        onClick={handleSubmit(onSaveAndSendHandler)}
        disabled={isLoading}
      >
        {t('sign_and_send')}
      </Button>
    </Stack>
  );
};
